export const slippageTypes = [
  {
    type: 'Negative Slippage',
    tagline: 'You get a worse price than you clicked',
    desc: 'Your order fills at a worse price than what was quoted on your screen. This is by far the most common type — every pip of negative slippage directly eats into your profit or adds to your loss. During major news events, 5–15 pips of negative slippage is normal on major pairs.',
    requestedPrice: '1.0850',
    executedPrice: '1.0852',
    pair: 'EURUSD',
    pipDiff: 2,
    costPerLot: 20,
    direction: 'negative' as const,
    scenarios: [
      { label: 'NFP Release', pips: '4.2', cost: '$42 / lot' },
      { label: 'Weekend Gap Open', pips: '8.5', cost: '$85 / lot' },
      { label: 'Thin Liquidity Hours', pips: '2.5', cost: '$25 / lot' },
    ],
    whoItHits: 'Market order traders during news events, low-liquidity sessions, and anyone trading exotic pairs with thin order books. Scalpers are disproportionately affected because they trade more frequently — even 0.5 pip per trade adds up fast.',
    howToHandle: 'Use limit orders for price certainty. Stay out of the market 5 minutes before and after major news releases. Stick to major pairs during London/NY overlap when order books are deepest.',
    frequencyPercent: 60,
    colorToken: 'accent' as const,
  },
  {
    type: 'Positive Slippage',
    tagline: 'You get a better price than you clicked',
    desc: 'The market moves in your favor between order submission and execution — your buy fills lower or your sell fills higher. The broker passes the price improvement to you instead of keeping the difference. Rare but real, especially with ECN brokers.',
    requestedPrice: '1.3120',
    executedPrice: '1.3118',
    pair: 'GBPUSD',
    pipDiff: 2,
    costPerLot: 20,
    direction: 'positive' as const,
    scenarios: [
      { label: 'Limit Order Fill', pips: '3.0', cost: '+$30 / lot' },
      { label: 'Rally Pullback', pips: '1.8', cost: '+$18 / lot' },
      { label: 'News Reversal', pips: '5.0', cost: '+$50 / lot' },
    ],
    whoItHits: 'Limit order traders during volatile conditions. ECN broker users see positive slippage more often because the broker has no incentive to keep the spread difference. Most common during fast directional moves where the price briefly overshoots and retraces.',
    howToHandle: 'Use limit orders to position for favorable fills. Choose ECN brokers that transparently pass through both positive and negative slippage. Fast market conditions create opportunity for price improvement — but never rely on it as a strategy.',
    frequencyPercent: 12,
    colorToken: 'primary' as const,
  },
  {
    type: 'Zero Slippage',
    tagline: 'You get exactly the price you clicked',
    desc: 'Perfect execution — your order fills at the precise quoted price with zero deviation. This is what every trader hopes for on every trade, and it is achievable with the right combination of broker, order type, and market conditions.',
    requestedPrice: '156.350',
    executedPrice: '156.350',
    pair: 'USDJPY',
    pipDiff: 0,
    costPerLot: 0,
    direction: 'neutral' as const,
    scenarios: [
      { label: 'London Session Majors', pips: '0.0', cost: '$0 / lot' },
      { label: 'ECN + Limit Orders', pips: '0.0', cost: '$0 / lot' },
      { label: 'Deep Liquidity Pairs', pips: '0.0', cost: '$0 / lot' },
    ],
    whoItHits: 'ECN/STP broker users trading major pairs during deep liquidity hours, especially those using limit orders. Most common during the London/NY overlap when bid-ask spreads are tightest and order books are deepest.',
    howToHandle: 'Trade during London/NY overlap (12:00–16:00 GMT). Use ECN or STP brokers with direct market access. Stick to major pairs — EURUSD, USDJPY, GBPUSD, AUDUSD — where dozens of liquidity providers compete to fill your order.',
    frequencyPercent: 28,
    colorToken: 'secondary' as const,
  },
] as const;

export const slippageTriggers = [
  {
    trigger: 'Major News Events',
    icon: 'ri-flashlight-line',
    desc: 'NFP, CPI, FOMC, and central bank rate decisions are the biggest slippage triggers. During NFP release, the market can move 30–50 pips in under one second. Market makers pull liquidity, widening spreads dramatically. A market order placed during NFP can slip 5–15 pips on major pairs and 20+ pips on crosses.',
    stats: 'NFP: avg 4.2 pips slippage on EURUSD (first 5 seconds)',
    severity: 'Critical',
  },
  {
    trigger: 'Session Transitions',
    icon: 'ri-sun-line',
    desc: 'When London closes (4 PM GMT) and NY is winding down, liquidity drops sharply. The spread widens and slippage increases because fewer banks are actively quoting. Similarly, the Sunday open (10 PM GMT) is notorious for gap-driven slippage as the market absorbs weekend news.',
    stats: 'London close to Tokyo open: 2–4× normal slippage',
    severity: 'High',
  },
  {
    trigger: 'Low-Liquidity Pairs',
    icon: 'ri-bar-chart-line',
    desc: 'Exotic pairs (USDTRY, USDMXN, EURHUF) and minor crosses have far fewer market makers. With only 3–5 active liquidity providers vs 40+ for EURUSD, the order book is thin. A 0.5 lot market order on USDTRY can move the price enough to cause 5–10 pip slippage.',
    stats: 'Exotics: 3–10× more slippage than majors on equivalent size',
    severity: 'High',
  },
  {
    trigger: 'Large Order Size',
    icon: 'ri-stack-line',
    desc: 'A 50-lot market order on EURUSD may consume multiple price levels. The first 10 lots fill at 1.0850, the next 20 at 1.0851, and the last 20 at 1.0852 — averaging 1.2 pips of slippage. This is called " walking the book" and is why institutional traders use algorithms to split large orders.',
    stats: '50 lots: avg 1.5 pips slippage vs 0.1 pip for 1 lot orders',
    severity: 'Moderate',
  },
] as const;

export const slippageScenarios = [
  {
    label: 'Calm Market',
    tradesPerMonth: 100,
    lotSize: 1.0,
    slippageRate: 0.1,
    description: 'Normal conditions during London session — minimal slippage.',
  },
  {
    label: 'News Trading',
    tradesPerMonth: 8,
    lotSize: 1.0,
    slippageRate: 4.0,
    description: 'Trading around major news events — significant slippage expected.',
  },
  {
    label: 'Night Trading',
    tradesPerMonth: 40,
    lotSize: 0.5,
    slippageRate: 1.5,
    description: 'Trading during Asian session lull — moderate but consistent slippage.',
  },
] as const;

export const slippageFAQs = [
  {
    question: 'What exactly is forex slippage?',
    answer: 'Slippage is the difference between the price you clicked and the price at which your order actually executes. It happens because there is a tiny delay between order submission and execution — and in that gap, the market price can change. Slippage can be negative (worse price), positive (better price), or zero (exact price). It is most common with market orders during volatile conditions and is a normal part of trading — not necessarily a sign of a bad broker.',
    category: 'Basics',
  },
  {
    question: 'Is slippage always bad?',
    answer: 'No. Positive slippage gives you a better fill than expected — you buy cheaper or sell higher than your intended price. This happens when the market moves in your favor during the execution window. However, positive slippage is less common than negative because market makers and brokers benefit from negative slippage (the difference goes to them or their liquidity provider). ECN brokers typically pass through both positive and negative slippage transparently.',
    category: 'Basics',
  },
  {
    question: 'What causes slippage during news events?',
    answer: 'During major news like NFP or FOMC, market makers pull their quotes to avoid being picked off by traders who have faster access to the news. Liquidity evaporates in milliseconds. A market order submitted during this vacuum has no resting orders to match against — it "walks" through multiple price levels until it finds enough liquidity. The result: 5–15 pips of slippage on major pairs is normal during the first 5 seconds of NFP release.',
    category: 'Causes',
  },
  {
    question: 'How can I reduce or avoid slippage?',
    answer: 'Five practical ways: (1) Use limit orders instead of market orders — your price is guaranteed but execution is not. (2) Trade during liquid sessions — London and NY overlap offers the deepest order books. (3) Avoid trading 5 minutes before and after major news events. (4) Use an ECN broker — they pass raw market prices without dealer intervention. (5) Split large orders into smaller chunks to avoid exhausting available liquidity at a single price level.',
    category: 'Prevention',
  },
  {
    question: 'Do ECN brokers have less slippage?',
    answer: 'Generally yes — but not because slippage disappears. ECN brokers pass your order directly to liquidity providers without a dealing desk. Market maker brokers may requote or reject your order during volatile conditions rather than filling it with slippage. ECN brokers give you the real market price, including any slippage that occurs. The key difference: ECN brokers are neutral — they do not profit from your slippage, so they have no incentive to manipulate it.',
    category: 'Broker Differences',
  },
  {
    question: 'Is slippage considered a hidden cost?',
    answer: 'Slippage straddles the line between visible and hidden. It is visible — you see the fill price on your statement and can compare it to the clicked price. But it is unpredictable — you cannot know the cost before entering a trade. For planning purposes, experienced traders budget 0.3–0.5 pips of slippage per trade during normal conditions and 3–5 pips during news events. If your broker consistently gives you worse slippage than expected, that is a red flag.',
    category: 'Cost Impact',
  },
  {
    question: 'How does slippage affect scalping vs swing trading?',
    answer: 'Slippage hits scalpers hardest because it is a per-trade cost. A scalper doing 15 trades/day with 0.5 pip average slippage loses 7.5 pips/day — often more than the spread. A swing trader doing 1 trade/day loses 0.5 pips/day — negligible compared to swap and spread. This is another reason scalpers need the tightest execution conditions: slippage is proportional to trading frequency, and scalpers trade the most.',
    category: 'Trading Impact',
  },
  {
    question: 'Can I measure my actual slippage?',
    answer: 'Yes. Most trading platforms (MT4/MT5, cTrader) log the requested price and the filled price for every order. Export your trade history to a spreadsheet and subtract the requested price from the fill price for each trade. Average the absolute values to get your typical slippage. Track this monthly — if your average slippage suddenly increases, it may indicate a change in your broker\'s execution quality or that you are trading during worse conditions.',
    category: 'Measurement',
  },
  {
    question: 'What is the difference between slippage and spread widening?',
    answer: 'Spread widening is the broker increasing the bid-ask gap. Slippage is your order filling at a different price than you clicked within that spread. They often happen together: during news, the spread widens from 1 pip to 8 pips, and within that 8-pip gap your order may slip 3 more pips. Both increase your effective cost. Spread widening is broker-controlled; slippage is market-driven. Together they can turn a "tight spread" broker into a very expensive one during volatile moments.',
    category: 'Basics',
  },
  {
    question: 'Are limit orders immune to slippage?',
    answer: 'Yes — limit orders guarantee your price. You will never get a worse fill than your limit price. The trade-off: your order may not fill at all if the market moves away from your limit. During fast markets, a limit order at 1.0850 might sit unfilled while the market trades at 1.0855. The choice is between price certainty (limit order) and execution certainty (market order). There is no free lunch — you trade one risk for the other.',
    category: 'Prevention',
  },
  {
    question: 'How do brokers handle slippage differently?',
    answer: 'ECN/STP brokers pass market slippage through transparently. Market makers may requote you during volatile conditions — showing a new price and asking if you accept it — rather than filling with slippage. Some brokers offer "zero slippage" guarantees on certain account types, but these accounts typically have wider spreads to compensate. Always read the broker\'s order execution policy: it must disclose their slippage handling procedures and any requote policies.',
    category: 'Broker Differences',
  },
  {
    question: 'What role does VPS hosting play in reducing slippage?',
    answer: 'A VPS (Virtual Private Server) places your trading platform physically close to your broker\'s servers, reducing latency from 50–200ms (home internet) to 1–5ms. For scalpers and algorithmic traders, this latency reduction can significantly decrease slippage — the faster your order reaches the market, the less time for the price to move. VPS is overkill for swing traders who are not sensitive to millisecond-level execution. Cost: $20–$50/month for a trading-grade VPS.',
    category: 'Prevention',
  },
] as const;

export const orderTypeComparison = [
  {
    label: 'Market Order',
    slug: 'market',
    summary: 'Execution certainty, price uncertainty',
    body: 'Market orders prioritize speed over price. Your broker fills the order at the best available price in the order book at that exact moment. During volatile conditions, the best available price may be several pips away from the price you saw on your screen — that gap is slippage. Market orders are the most vulnerable order type for slippage, especially during news events when liquidity thins and the order book depth collapses. The trade-off: you will be filled, but you may not like the price.',
    slippageRisk: 'High',
    fillGuarantee: 'Always fills',
    bestFor: 'Getting in or out fast — trend breakouts, news scalping, stop-loss execution',
    typicalSlippage: '0.1–4.0 pips depending on conditions',
  },
  {
    label: 'Limit Order',
    slug: 'limit',
    summary: 'Price certainty, execution uncertainty',
    body: 'Limit orders guarantee your price but not your execution. You specify the exact price at which you are willing to trade, and the order only fills if the market reaches that level. You will never experience negative slippage with a limit order — but you may not get filled at all. During fast-moving markets, a limit buy at 1.0850 might sit untouched while the market trades at 1.0855 and continues higher. The trade-off: perfect price, but the trade may never happen.',
    slippageRisk: 'Zero (price-guaranteed)',
    fillGuarantee: 'May not fill',
    bestFor: 'Entering at a precise level, fading spikes, building positions patiently',
    typicalSlippage: '0 pips (price is guaranteed)',
  },
  {
    label: 'Stop Order',
    slug: 'stop',
    summary: 'Trigger price, then market execution',
    body: 'A stop order becomes a market order once a specified trigger price is reached. The stop triggers at your chosen level, but the fill price is determined by the market — and during volatile conditions, the fill can be significantly worse than the trigger. This is the origin of the term "slippage" for many traders: a stop-loss at 1.0820 fills at 1.0810 during a news spike. The trade-off: you set the activation point, but the market sets the execution price.',
    slippageRisk: 'High (market fill after trigger)',
    fillGuarantee: 'Fills at trigger (as market order)',
    bestFor: 'Breakout entries, stop-loss protection, pending entries at key levels',
    typicalSlippage: '0.5–10+ pips during volatility',
  },
  {
    label: 'Stop-Limit Order',
    slug: 'stoplimit',
    summary: 'Trigger price, then limit execution',
    body: 'A stop-limit order combines the two: a trigger level that activates the order, and a limit price that caps how far the fill can deviate. When the trigger is hit, the order becomes a limit order rather than a market order. This protects you from extreme slippage — if the market gaps past your limit, the order simply does not fill. The trade-off: you avoid catastrophic fills, but in a fast crash your protective order may never execute, leaving you exposed to far larger losses than slippage would have caused.',
    slippageRisk: 'Capped by limit offset',
    fillGuarantee: 'May not fill if market gaps',
    bestFor: 'Precise stop-loss protection during known volatile events, careful breakout entries',
    typicalSlippage: '0 to limit-offset pips (capped)',
  },
] as const;

export const brokerExecutionModels = [
  {
    model: 'ECN (Electronic Communication Network)',
    tag: 'Direct Market Access',
    verdict: 'The broker is a neutral pipe — slippage is pure market physics, nothing more.',
    bodyParagraphs: [
      'ECN brokers route your order directly into a network of liquidity providers — banks, hedge funds, and other traders. There is no dealing desk, and the broker earns a fixed commission per trade regardless of whether you win or lose.',
      'Positive slippage is passed through in full, so ECN traders occasionally get price improvements. Most institutional and professional traders use ECN accounts for exactly this reason.',
    ],
    slippageStance: 'Transparent — market-driven only',
    positiveSlippage: 'Yes — passed through to trader',
    commission: 'Yes — typically $3–$7 per lot per side',
  },
  {
    model: 'STP (Straight Through Processing)',
    tag: 'Intermediary Routing',
    verdict: 'Your order reaches the market — but which market depends on who the broker partners with.',
    bodyParagraphs: [
      'STP brokers sit between you and the liquidity providers, aggregating quotes from multiple sources and routing your order to the best one. They do not trade against you, but some add a small markup to the spread instead of charging a visible commission.',
      'Execution quality hinges on the broker\'s liquidity provider relationships. A STP broker partnered with deep-order-book banks will perform nearly as well as ECN — one with weaker partners will not.',
    ],
    slippageStance: 'Mostly market-driven, routing-dependent',
    positiveSlippage: 'Sometimes — depends on broker policy',
    commission: 'Often built into spread (no separate fee)',
  },
  {
    model: 'Market Maker (Dealing Desk)',
    tag: 'Counterparty Model',
    verdict: 'The broker is your trade counterparty — and it sets the rules.',
    bodyParagraphs: [
      'When you buy, the market maker sells to you from its own inventory. During calm conditions this means fixed spreads and instant fills. During volatile conditions, the broker may widen spreads, requote at a worse price, or reject your order — because filling it would cost them money.',
      'Some market makers profit from negative slippage: if you click buy at 1.0850 and the broker fills you at 1.0852, that 2-pip spread goes to the broker, not the interbank market. Positive slippage is almost never passed through.',
    ],
    slippageStance: 'Broker-controlled — may requote or reject',
    positiveSlippage: 'Rare — broker captures price improvement',
    commission: 'Typically none — broker earns from spread',
  },
] as const;

export const slippageReductionStrategies = [
  {
    title: 'Use limit orders for entries',
    insight: 'Market orders are the primary source of slippage. A limit order costs nothing extra and guarantees your price — the trade-off is that you may not get filled if the market runs away from your level.',
    impact: 'Eliminates entry slippage entirely',
    cost: 'Risk of missed entries',
  },
  {
    title: 'Trade during liquid sessions',
    insight: 'The London/NY overlap (12:00–16:00 GMT) offers the deepest order books across all major pairs. Slippage during these hours averages 0.1–0.3 pips on EURUSD — during the Asian session lull, the same trade can slip 1–2 pips.',
    impact: 'Reduces slippage by 60–80% vs off-peak',
    cost: 'Requires scheduling around session hours',
  },
  {
    title: 'Avoid news releases by a 5-minute buffer',
    insight: 'The first 5 seconds of NFP or FOMC can produce 10–50 pip moves. Even after the initial spike, spreads remain wide for several minutes. Stay out 5 minutes before and after scheduled high-impact news. Use an economic calendar — it is free and takes 30 seconds to check.',
    impact: 'Avoids the highest-slippage moments entirely',
    cost: 'Missed news-trading opportunities',
  },
  {
    title: 'Stick to major pairs',
    insight: 'EURUSD has 40+ liquidity providers competing to fill your order. USDTRY might have 3–5. The difference in order book depth means a 1-lot market order on EURUSD slips less than 0.1 pips — the same order on USDTRY can slip 5–10 pips simply because there is no resting liquidity at the next price level.',
    impact: '3–10× less slippage vs exotic pairs',
    cost: 'Narrower universe of tradeable instruments',
  },
  {
    title: 'Split large orders',
    insight: 'A 50-lot market order on EURUSD will walk through 3–4 price levels, averaging 1–2 pips of slippage. Splitting it into 10 orders of 5 lots each, spaced 30 seconds apart, lets the order book replenish between fills. The cost: slightly more spread paid on each fill. The benefit: dramatically lower slippage.',
    impact: 'Cuts large-order slippage by 50–70%',
    cost: 'Slightly higher spread cost on split fills',
  },
  {
    title: 'Choose an ECN or STP broker',
    insight: 'Market makers profit from your slippage. ECN brokers earn a fixed commission regardless of your fill price — they have zero incentive to manipulate execution. Over 100 trades, the difference between an honest ECN and a slippage-maximizing market maker can be hundreds of dollars.',
    impact: 'Eliminates broker-driven slippage',
    cost: 'Commission per trade ($3–$7/lot)',
  },
  {
    title: 'Consider a VPS for algorithmic trading',
    insight: 'Home internet adds 50–200ms of latency between your platform and the broker. A trading VPS colocated near your broker\'s servers drops that to 1–5ms. For manual traders this is irrelevant. For a scalping algorithm executing 50 trades/day, 50ms of extra latency means more time for the market to move — and more slippage per trade.',
    impact: 'Reduces latency-driven slippage for algo traders',
    cost: '$20–$50/month for a trading VPS',
  },
] as const;

export const relatedSlippageGuides = [
  {
    title: 'Spread',
    href: '/forex-spread/',
    desc: 'The bid-ask gap is where every trade begins — and it varies wildly by pair, session, and broker. Here\'s what you need to know.',
    icon: 'ri-arrow-left-right-line',
  },
  {
    title: 'Commission',
    href: '/forex-commission/',
    desc: 'Paying a flat commission might actually save you money compared to inflated spreads. See how to compare the two.',
    icon: 'ri-bank-card-line',
  },
  {
    title: 'Swap / Rollover',
    href: '/forex-swap/',
    desc: 'Holding overnight? Interest adds up fast — especially on exotic pairs. A few days of swap can cost more than the spread.',
    icon: 'ri-moon-clear-line',
  },
  {
    title: 'Hidden Charges',
    href: '/hidden-forex-trading-costs/',
    desc: 'Your trade ticket looks clean, but your account balance tells a different story. These hidden fees add up month after month.',
    icon: 'ri-eye-off-line',
  },
] as const;